import { type BlockType, PageMetaSchema } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@aurumiq/ui/components/dropdown-menu";
import { toast } from "@aurumiq/ui/components/sonner";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmyTime, relativeTime } from "@aurumiq/ui/lib/format";
import { cn } from "@aurumiq/ui/lib/utils";
import {
  Eye,
  EyeOff,
  History,
  LayoutTemplate,
  Newspaper,
  Rocket,
  Save,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import type { CmsPageDetail, CmsPreviewToken } from "@/lib/types";
import { usePreviewToken, usePublishPage, useRevertPage, useSaveDraft } from "../queries";
import { BlockForm } from "./block-form";
import { BlockList } from "./block-list";
import {
  buildPreviewPage,
  type EditorBlock,
  newBlock,
  newBlockId,
  toEditorBlocks,
  validateBlock,
} from "./blocks";
import { PostPanel } from "./post-panel";
import { PreviewPane } from "./preview-pane";
import { ResizeHandle, useColumnSizes } from "./resizable-columns";
import { SeoPanel } from "./seo-panel";

type Selection = { kind: "block"; key: string } | { kind: "seo" } | { kind: "post" };

export function PageEditor({ page, backTo }: { page: CmsPageDetail; backTo: string }) {
  const canWrite = usePermission("cms:write");
  const save = useSaveDraft(page.id);
  const publish = usePublishPage(page.id);
  const revert = useRevertPage(page.id);
  const preview = usePreviewToken(page.id);

  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [blocks, setBlocks] = useState<EditorBlock[]>(() => toEditorBlocks(page.draft.blocks));
  const [meta, setMeta] = useState<Record<string, unknown>>(page.draft.meta ?? {});
  const [excerpt, setExcerpt] = useState(page.excerpt ?? "");
  const isPost = page.kind === "post" || page.kind === "changelog";
  const [selected, setSelected] = useState<Selection>(() =>
    blocks[0] ? { kind: "block", key: blocks[0].key } : { kind: "seo" },
  );
  const [dirty, setDirty] = useState(false);
  const [previewOn, setPreviewOn] = useState(false);
  const [token, setToken] = useState<CmsPreviewToken | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [revertTo, setRevertTo] = useState<string | null>(null);
  const cols = useColumnSizes();

  // Reset local state when a new revision arrives from the server (save / revert).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on the revision id
  useEffect(() => {
    setTitle(page.title);
    setSlug(page.slug);
    const eb = toEditorBlocks(page.draft.blocks);
    setBlocks(eb);
    setMeta(page.draft.meta ?? {});
    setExcerpt(page.excerpt ?? "");
    setDirty(false);
    setSelected((s) =>
      s.kind === "block" && !eb.some((b) => b.key === s.key)
        ? eb[0]
          ? { kind: "block", key: eb[0].key }
          : { kind: "seo" }
        : s,
    );
  }, [page.draft.revision_id]);

  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const requestToken = () => preview.mutate(undefined, { onSuccess: setToken });
  const togglePreview = () => {
    const next = !previewOn;
    setPreviewOn(next);
    if (next && !token) requestToken();
  };

  const validations = useMemo(() => blocks.map((b) => validateBlock(b.raw)), [blocks]);
  const invalidCount = validations.filter((v) => !v.ok).length;
  const metaParsed = useMemo(() => PageMetaSchema.safeParse(meta), [meta]);
  const previewPage = useMemo(
    () => buildPreviewPage({ slug, kind: page.kind, title, blocks, meta }),
    [slug, page.kind, title, blocks, meta],
  );

  const mutateBlocks = (fn: (prev: EditorBlock[]) => EditorBlock[]) => {
    setBlocks(fn);
    setDirty(true);
  };
  const addBlock = (type: BlockType) => {
    const raw = newBlock(type);
    const key = `${raw.id}#new-${Date.now()}`;
    mutateBlocks((prev) => [...prev, { key, raw }]);
    setSelected({ kind: "block", key });
  };
  const updateBlock = (key: string, raw: Record<string, unknown>) =>
    mutateBlocks((prev) => prev.map((b) => (b.key === key ? { ...b, raw } : b)));
  const deleteBlock = (key: string) => {
    mutateBlocks((prev) => prev.filter((b) => b.key !== key));
    setSelected((s) => (s.kind === "block" && s.key === key ? { kind: "seo" } : s));
  };
  const duplicateBlock = (key: string) =>
    mutateBlocks((prev) => {
      const i = prev.findIndex((b) => b.key === key);
      const src = prev[i];
      if (!src) return prev;
      const type = typeof src.raw.type === "string" ? src.raw.type : "block";
      const copy: EditorBlock = {
        key: `${key}#copy-${Date.now()}`,
        raw: { ...structuredClone(src.raw), id: newBlockId(type) },
      };
      return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
    });
  const toggleHidden = (key: string) =>
    mutateBlocks((prev) =>
      prev.map((b) =>
        b.key === key ? { ...b, raw: { ...b.raw, hidden: b.raw.hidden !== true } } : b,
      ),
    );

  const canSave = canWrite && invalidCount === 0 && metaParsed.success && !save.isPending;

  const doSave = async () => {
    if (invalidCount > 0) {
      toast.error(
        `Fix or delete ${invalidCount} invalid block${invalidCount > 1 ? "s" : ""} first`,
      );
      return null;
    }
    if (!metaParsed.success) {
      toast.error("SEO fields are invalid");
      return null;
    }
    const validated = validations.map((v) => v.block);
    return save.mutateAsync({
      blocks: validated,
      meta: metaParsed.data,
      title: title !== page.title ? title : undefined,
      slug: slug !== page.slug ? slug : undefined,
      excerpt: excerpt !== (page.excerpt ?? "") ? excerpt : undefined,
    });
  };

  const selectedBlock =
    selected.kind === "block" ? blocks.find((b) => b.key === selected.key) : null;

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {title || "Untitled"}
            <StatusTag variant={statusVariant(page.published ? "published" : "draft")}>
              {page.published ? "PUBLISHED" : "DRAFT"}
            </StatusTag>
            {dirty ? <StatusTag variant="warn">UNSAVED</StatusTag> : null}
            {invalidCount ? <StatusTag variant="err">{invalidCount} INVALID</StatusTag> : null}
          </span>
        }
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            <Link to={backTo} className="text-primary hover:underline">
              ← Back
            </Link>
            <span className="font-mono">/{slug}</span>
            <span>· {page.kind}</span>
            <span>
              · draft v{page.draft.version}
              {page.published ? ` · live v${page.published.version}` : ""}
            </span>
          </span>
        }
        actions={
          <>
            {canWrite ? (
              <Button asChild variant="default">
                <Link to={`/cms/pages/${page.id}/design`}>
                  <LayoutTemplate /> Open designer
                </Link>
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <History /> Revisions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Revisions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {page.revisions.length === 0 ? (
                  <DropdownMenuItem disabled>No revisions yet</DropdownMenuItem>
                ) : (
                  page.revisions.map((r) => (
                    <DropdownMenuItem
                      key={r.revision_id}
                      disabled={!canWrite || r.revision_id === page.draft.revision_id}
                      onSelect={() => setRevertTo(r.revision_id)}
                    >
                      <div className="flex w-full items-center gap-2">
                        <span className="font-mono text-xs">v{r.version}</span>
                        <span className="min-w-0 flex-1 truncate">{r.note ?? "—"}</span>
                        {r.revision_id === page.published?.revision_id ? (
                          <StatusTag variant="ok">LIVE</StatusTag>
                        ) : null}
                        {r.revision_id === page.draft.revision_id ? (
                          <StatusTag>CURRENT</StatusTag>
                        ) : null}
                        <span
                          className="text-muted-foreground text-xs"
                          title={dmyTime(r.created_at)}
                        >
                          {relativeTime(r.created_at)}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={togglePreview} aria-pressed={previewOn}>
              {previewOn ? <EyeOff /> : <Eye />} Preview
            </Button>
            {canWrite ? (
              <>
                <Button onClick={() => doSave()} disabled={!canSave || !dirty}>
                  <Save /> {save.isPending ? "Saving…" : "Save draft"}
                </Button>
                <Button
                  variant="gold"
                  onClick={() => setPublishOpen(true)}
                  disabled={invalidCount > 0 || publish.isPending}
                >
                  <Rocket /> Publish
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div
        className={cn(
          "grid gap-4 lg:gap-2",
          previewOn
            ? "lg:grid-cols-[var(--rail)_auto_minmax(16rem,1fr)_auto_var(--preview)]"
            : "lg:grid-cols-[var(--rail)_auto_1fr]",
        )}
        style={
          {
            "--rail": `${cols.sizes.rail}px`,
            "--preview": `${cols.sizes.preview}px`,
          } as React.CSSProperties
        }
      >
        <aside className="grid min-w-0 content-start gap-3">
          <BlockList
            blocks={blocks}
            selectedKey={selected.kind === "block" ? selected.key : null}
            onSelect={(key) => setSelected({ kind: "block", key })}
            onReorder={(next) => mutateBlocks(() => next)}
            onAdd={addBlock}
            onToggleHidden={toggleHidden}
            onDuplicate={duplicateBlock}
            onDelete={deleteBlock}
          />
          <Button
            variant={selected.kind === "seo" ? "secondary" : "ghost"}
            className="justify-start"
            onClick={() => setSelected({ kind: "seo" })}
          >
            <Search /> Page &amp; SEO
          </Button>
          {isPost ? (
            <Button
              variant={selected.kind === "post" ? "secondary" : "ghost"}
              className="justify-start"
              onClick={() => setSelected({ kind: "post" })}
            >
              <Newspaper /> Post
            </Button>
          ) : null}
        </aside>
        <ResizeHandle
          column="rail"
          value={cols.sizes.rail}
          grow="right"
          onChange={(v) => cols.set("rail", v)}
          onReset={() => cols.reset("rail")}
          label="Resize blocks rail"
        />

        <section className="min-w-0 rounded-xl border bg-card p-4">
          {selected.kind === "post" ? (
            <PostPanel
              excerpt={excerpt}
              meta={meta}
              onExcerpt={(e) => {
                setExcerpt(e);
                setDirty(true);
              }}
              onMeta={(m) => {
                setMeta(m);
                setDirty(true);
              }}
              metaIssues={metaParsed.success ? undefined : metaParsed.error.issues}
            />
          ) : selected.kind !== "block" || !selectedBlock ? (
            <SeoPanel
              title={title}
              slug={slug}
              meta={meta}
              onTitle={(t) => {
                setTitle(t);
                setDirty(true);
              }}
              onSlug={(s) => {
                setSlug(s);
                setDirty(true);
              }}
              onMeta={(m) => {
                setMeta(m);
                setDirty(true);
              }}
              metaIssues={metaParsed.success ? undefined : metaParsed.error.issues}
            />
          ) : (
            <BlockForm
              key={selectedBlock.key}
              block={selectedBlock}
              onChange={(raw) => updateBlock(selectedBlock.key, raw)}
              onDelete={() => deleteBlock(selectedBlock.key)}
            />
          )}
        </section>

        {previewOn ? (
          <>
            <ResizeHandle
              column="preview"
              value={cols.sizes.preview}
              grow="left"
              onChange={(v) => cols.set("preview", v)}
              onReset={() => cols.reset("preview")}
              label="Resize preview"
            />
            <PreviewPane
              token={token}
              page={previewPage}
              onRefreshToken={requestToken}
              className="min-w-0"
            />
          </>
        ) : null}
      </div>

      <ReasonDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title={`Publish “${title}”?`}
        description={
          dirty
            ? "Unsaved changes are saved as a new revision first, then that revision goes live."
            : `Revision v${page.draft.version} goes live on the landing site.`
        }
        confirmLabel="Publish"
        confirmVariant="default"
        minLength={0}
        reasonLabel="Note"
        onConfirm={async (reason) => {
          let revisionId = page.draft.revision_id;
          if (dirty) {
            const saved = await doSave();
            if (!saved) throw new Error("Save failed");
            revisionId = saved.draft.revision_id;
          }
          await publish.mutateAsync({ revision_id: revisionId, reason: reason || undefined });
        }}
      />
      <ReasonDialog
        open={revertTo !== null}
        onOpenChange={(o) => !o && setRevertTo(null)}
        title="Revert draft to this revision?"
        description="The draft is replaced by a copy of the selected revision. Unsaved changes are lost; the live page is unaffected until you publish."
        confirmLabel="Revert"
        minLength={0}
        reasonLabel="Note"
        onConfirm={async () => {
          if (revertTo) await revert.mutateAsync(revertTo);
        }}
      />
    </>
  );
}
